/**
 * Recommendation Engine — Pipeline layer 5 (Lotes 1-6 training, §5).
 *
 * Evaluates the 20 WHEN-RECOMMEND-BECAUSE rules after each tool call.
 * Suppression: a recommendation that the user dismissed will not be
 * re-emitted for 30 minutes. `conversationGoal` modulates priority:
 *   - troubleshooting → boosts anomaly-driven (REC-01..07)
 *   - reporting       → boosts status-driven (REC-14..15)
 *   - exploring       → boosts cross-entity (REC-12..13)
 */

import type {
  ConversationContext,
  PendingRecommendation,
} from '@/agent/conversationContext';
import { isRecDismissed } from '@/agent/conversationContext';

// ---------------------------------------------------------------------------
// Trigger payload — describes the just-executed tool and surrounding facts
// ---------------------------------------------------------------------------
export interface RecommendationTrigger {
  intent: string;
  slots: Record<string, unknown>;
  /** Facts about the entity the agent just navigated to (anomaly flags, etc.) */
  facts: {
    cameraId?: string;
    edgeId?: string;
    woId?: string;
    healthStatus?: 'ok' | 'warning' | 'critical' | 'offline';
    edgeOwner?: string;
    edgeKpiMaxPct?: number; // 0..100
    defectRatePct?: number;
    woHasBackorders?: boolean;
    woBackorderCount?: number;
    woHasActiveNcs?: boolean;
    woStatus?: string;
    woDueInDays?: number;
    firmwareKind?: 'security' | 'feature' | 'bugfix';
    firmwareDaysSinceRelease?: number;
    firmwareInstalled?: boolean;
    deviceType?: string;
    breachRowCount?: number;
    firstBreachId?: string;
    nodeStatus?: 'ok' | 'warning' | 'critical';
    nodeId?: string;
    nodeType?: 'camera' | 'edge' | 'plc';
    rms?: number;
    bomBackorderRows?: number;
    inProgressWoCount?: number;
    inProgressWoId?: string;
  };
}

export type Priority = 'high' | 'medium' | 'low';

interface RuleDefinition {
  id: string;
  category: 'anomaly' | 'pattern' | 'cross_entity' | 'status' | 'navigation' | 'intent';
  priority: Priority;
  /** Returns the recommendation if the rule fires, null otherwise. */
  evaluate: (
    t: RecommendationTrigger,
    ctx: ConversationContext,
  ) => Omit<PendingRecommendation, 'priority' | 'shownAt' | 'id'> | null;
}

const fmt = (msg: string, params: Record<string, string | number | undefined>) =>
  msg.replace(/\{(\w+)\}/g, (_, k) => String(params[k] ?? `{${k}}`));

// ---------------------------------------------------------------------------
// Rule catalogue — order is informative; priority is set by category + ctx
// ---------------------------------------------------------------------------
const RULES: RuleDefinition[] = [
  // ── Anomaly-driven ──────────────────────────────────────────
  {
    id: 'REC-01',
    category: 'anomaly',
    priority: 'high',
    evaluate: (t) => {
      if (
        t.intent !== 'devices_open_camera_detail' ||
        !t.facts.cameraId ||
        !['warning', 'critical'].includes(t.facts.healthStatus ?? '')
      )
        return null;
      return {
        message: 'Esta cámara tiene una alerta activa. ¿Quieres ver el diagnóstico?',
        intent: 'devices_open_camera_health',
        slots: { cameraId: t.facts.cameraId },
      };
    },
  },
  {
    id: 'REC-02',
    category: 'anomaly',
    priority: 'high',
    evaluate: (t) => {
      if (
        t.intent !== 'devices_open_camera_health' ||
        t.facts.healthStatus !== 'critical' ||
        !t.facts.edgeOwner
      )
        return null;
      return {
        message: fmt(
          'Veo alertas críticas. ¿Reviso las métricas del edge propietario ({edgeOwner}) por si hay correlación?',
          { edgeOwner: t.facts.edgeOwner },
        ),
        intent: 'devices_open_edge_metrics',
        slots: { edgeId: t.facts.edgeOwner },
      };
    },
  },
  {
    id: 'REC-03',
    category: 'anomaly',
    priority: 'medium',
    evaluate: (t) => {
      if (
        t.intent !== 'devices_open_edge_metrics' ||
        !t.facts.edgeId ||
        (t.facts.edgeKpiMaxPct ?? 0) < 90
      )
        return null;
      return {
        message:
          'Detecto carga alta. ¿Reviso el histórico 24h para ver si es puntual o sostenido?',
        intent: 'devices_open_edge_metrics',
        slots: { edgeId: t.facts.edgeId, range: '24h' },
      };
    },
  },
  {
    id: 'REC-04',
    category: 'anomaly',
    priority: 'medium',
    evaluate: (t) => {
      if (
        t.intent !== 'devices_open_camera_history' ||
        !t.facts.cameraId ||
        (t.facts.defectRatePct ?? 0) < 15
      )
        return null;
      return {
        message:
          'La tasa de defectos es elevada. ¿Reviso la calibración? Un RMS alto puede indicar el problema.',
        intent: 'devices_open_camera_calibration',
        slots: { cameraId: t.facts.cameraId },
      };
    },
  },
  {
    id: 'REC-05',
    category: 'anomaly',
    priority: 'high',
    evaluate: (t) => {
      if (
        t.intent !== 'open_wo_detail' ||
        !t.facts.woId ||
        !t.facts.woHasBackorders
      )
        return null;
      return {
        message: fmt(
          'Esta WO tiene {n} items en backorder. ¿Reviso la BOM filtrada?',
          { n: t.facts.woBackorderCount ?? 1 },
        ),
        intent: 'open_wo_bom',
        slots: { woId: t.facts.woId, filterStatus: 'backorder' },
      };
    },
  },
  {
    id: 'REC-06',
    category: 'anomaly',
    priority: 'high',
    evaluate: (t) => {
      if (
        t.intent !== 'open_wo_detail' ||
        !t.facts.woId ||
        (t.facts.woDueInDays ?? 99) >= 3 ||
        t.facts.woStatus === 'completed'
      )
        return null;
      return {
        message: fmt('WO con due date crítico ({days}d). ¿Reviso el timeline reciente?', {
          days: t.facts.woDueInDays ?? 0,
        }),
        intent: 'open_wo_timeline',
        slots: { woId: t.facts.woId, range: '24h' },
      };
    },
  },
  {
    id: 'REC-07',
    category: 'anomaly',
    priority: 'high',
    evaluate: (t) => {
      if (
        t.intent !== 'open_wo_detail' ||
        !t.facts.woId ||
        !t.facts.woHasActiveNcs
      )
        return null;
      return {
        message:
          'WO con NCs activas. ¿Reviso el timeline filtrado a eventos de NC?',
        intent: 'open_wo_timeline',
        slots: { woId: t.facts.woId, eventType: 'nc_raised' },
      };
    },
  },
  // ── Pattern-driven ─────────────────────────────────────────
  {
    id: 'REC-08',
    category: 'pattern',
    priority: 'medium',
    evaluate: (_t, ctx) => {
      const recentCams = ctx.recentEntities
        .filter((r) => r.type === 'camera' && Date.now() - r.ts < 5 * 60_000)
        .slice(0, 4);
      if (recentCams.length < 2) return null;
      return {
        message:
          'Veo que estás revisando varias cámaras. ¿Quieres compararlas lado a lado?',
        intent: 'devices_open_camera_compare',
        slots: { cameraIds: recentCams.map((r) => r.id) },
      };
    },
  },
  {
    id: 'REC-09',
    category: 'pattern',
    priority: 'low',
    evaluate: (t, ctx) => {
      if (!t.facts.cameraId) return null;
      const tenMinAgo = Date.now() - 10 * 60_000;
      const visits = ctx.turnHistory.filter(
        (h) =>
          h.ts >= tenMinAgo &&
          (h.slots?.cameraId as string) === t.facts.cameraId,
      ).length;
      if (visits < 3) return null;
      const visitedHealth = ctx.turnHistory.some(
        (h) => h.toolCalled === 'devices_open_camera_health',
      );
      if (visitedHealth) return null;
      return {
        message: fmt(
          'Veo que profundizas en {cameraId}. ¿Reviso su salud por si hay un patrón?',
          { cameraId: t.facts.cameraId },
        ),
        intent: 'devices_open_camera_health',
        slots: { cameraId: t.facts.cameraId },
      };
    },
  },
  {
    id: 'REC-10',
    category: 'pattern',
    priority: 'medium',
    evaluate: (t) => {
      if (
        t.intent !== 'devices_open_sla' ||
        !t.slots?.breachOnly ||
        (t.facts.breachRowCount ?? 0) < 1 ||
        !t.facts.firstBreachId
      )
        return null;
      return {
        message: fmt('El dispositivo más crítico es {id}. ¿Lo abro?', {
          id: t.facts.firstBreachId,
        }),
        intent: 'devices_open_camera_detail',
        slots: { cameraId: t.facts.firstBreachId },
      };
    },
  },
  {
    id: 'REC-11',
    category: 'pattern',
    priority: 'low',
    evaluate: (t) => {
      if (
        t.intent !== 'open_wo_bom' ||
        t.slots?.filterStatus !== 'backorder' ||
        (t.facts.bomBackorderRows ?? 1) > 0
      )
        return null;
      return {
        message:
          'No hay backorders. ¿Quieres revisar el patrón de consumos en timeline?',
        intent: 'open_wo_timeline',
        slots: { woId: t.facts.woId, eventType: 'material_consumed' },
      };
    },
  },
  // ── Cross-entity ───────────────────────────────────────────
  {
    id: 'REC-12',
    category: 'cross_entity',
    priority: 'low',
    evaluate: (t) => {
      if (t.intent !== 'devices_open_camera_detail' || !t.facts.edgeOwner) return null;
      return {
        message: fmt(
          'Esta cámara está conectada a {edgeOwner}. ¿Quieres ver el edge?',
          { edgeOwner: t.facts.edgeOwner },
        ),
        intent: 'devices_open_edge_detail',
        slots: { edgeId: t.facts.edgeOwner },
      };
    },
  },
  {
    id: 'REC-13',
    category: 'cross_entity',
    priority: 'low',
    evaluate: (t) => {
      if (t.intent !== 'open_wo_detail' || !t.facts.woId) return null;
      const lineId = (t.slots?.lineId as string) ?? 'LINE-A';
      return {
        message: fmt('Esta WO está en {lineId}. ¿Quieres verla en el mapa de planta?', {
          lineId,
        }),
        intent: 'open_floor_map',
        slots: { highlight: lineId },
      };
    },
  },
  // ── Status-driven ──────────────────────────────────────────
  {
    id: 'REC-14',
    category: 'status',
    priority: 'medium',
    evaluate: (t) => {
      if (
        t.intent !== 'open_wo_detail' ||
        !t.facts.woId ||
        t.facts.woStatus !== 'planned'
      )
        return null;
      return {
        message: 'WO en planned. ¿Verifico los materiales antes de liberar?',
        intent: 'open_wo_bom',
        slots: { woId: t.facts.woId },
      };
    },
  },
  {
    id: 'REC-15',
    category: 'status',
    priority: 'high',
    evaluate: (t) => {
      if (
        t.intent !== 'devices_open_firmware' ||
        t.facts.firmwareKind !== 'security' ||
        (t.facts.firmwareDaysSinceRelease ?? 0) <= 30 ||
        t.facts.firmwareInstalled
      )
        return null;
      return {
        message:
          'La actualización de seguridad lleva > 30 días sin instalar. ¿Programo OTA?',
        intent: 'schedule_firmware_ota',
        slots: {},
      };
    },
  },
  // ── Context-aware navigation ──────────────────────────────
  {
    id: 'REC-16',
    category: 'navigation',
    priority: 'medium',
    evaluate: (t, ctx) => {
      const prevCalib = ctx.turnHistory
        .slice(-3)
        .find((h) => h.toolCalled === 'devices_open_camera_calibration');
      if (
        t.intent !== 'devices_open_camera_history' ||
        !prevCalib ||
        (t.facts.rms ?? 0) <= 1.0
      )
        return null;
      return {
        message:
          'RMS alto puede causar falsos positivos en alguna clase. ¿Filtro por la clase más probable?',
        intent: 'set_filter',
        slots: { field: 'class', value: 'flexion' },
      };
    },
  },
  {
    id: 'REC-17',
    category: 'navigation',
    priority: 'high',
    evaluate: (t) => {
      if (
        t.intent !== 'select_topology_node' ||
        !t.facts.nodeId ||
        t.facts.nodeStatus !== 'warning'
      )
        return null;
      const nextIntent =
        t.facts.nodeType === 'edge' ? 'devices_open_edge_metrics' : 'devices_open_camera_health';
      return {
        message: fmt('Nodo {id} en warning. ¿Abro su diagnóstico?', {
          id: t.facts.nodeId,
        }),
        intent: nextIntent,
        slots:
          t.facts.nodeType === 'edge'
            ? { edgeId: t.facts.nodeId }
            : { cameraId: t.facts.nodeId },
      };
    },
  },
  {
    id: 'REC-18',
    category: 'navigation',
    priority: 'low',
    evaluate: (t) => {
      if (
        t.intent !== 'click_floor_map_marker' ||
        !t.facts.nodeId ||
        t.facts.nodeStatus !== 'warning'
      )
        return null;
      return {
        message: fmt(
          'Para ver dependencias de red de {id}, ¿abro la topología?',
          { id: t.facts.nodeId },
        ),
        intent: 'open_network_topology',
        slots: { focus: t.facts.nodeId },
      };
    },
  },
  // ── User intent inference ─────────────────────────────────
  {
    id: 'REC-19',
    category: 'intent',
    priority: 'low',
    evaluate: (_t, ctx) => {
      if (ctx.conversationGoal !== 'troubleshooting') return null;
      const lastThree = ctx.turnHistory.slice(-3);
      if (lastThree.length < 3) return null;
      const noProgress = lastThree.every(
        (h) => !h.toolCalled || h.toolCalled === 'unknown',
      );
      if (!noProgress) return null;
      return {
        message:
          '¿Te ayudo desde una vista global? Puedo abrir el SLA o la topología para tener contexto general.',
        intent: 'devices_open_sla',
        slots: {},
      };
    },
  },
  {
    id: 'REC-20',
    category: 'intent',
    priority: 'high',
    evaluate: (t) => {
      if (
        t.intent !== 'ask_about_wo_vague' ||
        (t.facts.inProgressWoCount ?? 0) !== 1 ||
        !t.facts.inProgressWoId
      )
        return null;
      return {
        message: fmt('Solo hay una WO en curso ({woId}). La abro.', {
          woId: t.facts.inProgressWoId,
        }),
        intent: 'open_wo_detail',
        slots: { woId: t.facts.inProgressWoId },
      };
    },
  },
];

// ---------------------------------------------------------------------------
// Goal-aware priority modulation (§5.3)
// ---------------------------------------------------------------------------
const PRIORITY_RANK: Record<Priority, number> = { high: 3, medium: 2, low: 1 };

function modulatePriority(
  rule: RuleDefinition,
  ctx: ConversationContext,
): Priority {
  if (
    ctx.conversationGoal === 'troubleshooting' &&
    rule.category === 'anomaly'
  ) {
    return 'high';
  }
  if (ctx.conversationGoal === 'reporting' && rule.category === 'status') {
    return rule.priority === 'low' ? 'medium' : rule.priority;
  }
  if (
    ctx.conversationGoal === 'exploring' &&
    rule.category === 'cross_entity'
  ) {
    return rule.priority === 'low' ? 'medium' : rule.priority;
  }
  return rule.priority;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
export interface EvaluatedRecommendation extends PendingRecommendation {
  category: RuleDefinition['category'];
}

export function evaluateRecommendations(
  trigger: RecommendationTrigger,
  ctx: ConversationContext,
  now = Date.now(),
): EvaluatedRecommendation | null {
  const candidates: EvaluatedRecommendation[] = [];

  for (const rule of RULES) {
    if (isRecDismissed(ctx, rule.id, now)) continue;
    const out = rule.evaluate(trigger, ctx);
    if (!out) continue;
    candidates.push({
      id: rule.id,
      message: out.message,
      intent: out.intent,
      slots: out.slots,
      priority: modulatePriority(rule, ctx),
      category: rule.category,
      shownAt: now,
    });
  }

  if (candidates.length === 0) return null;
  candidates.sort(
    (a, b) => PRIORITY_RANK[b.priority] - PRIORITY_RANK[a.priority],
  );
  return candidates[0];
}

export const RECOMMENDATION_RULE_COUNT = RULES.length;
